import React, { useState, useEffect } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { 
  ArrowRight,
  Search,
  Trophy,
  CheckCircle2,
  Star,
  MessageSquare,
  ChevronRight
} from 'lucide-react';
import DesignerCard from '../components/DesignerCard';
import { fetchTemplates, fetchTopDesigners, fetchCompetitions, fetchFeaturedTemplates } from '../api/client';
import TemplateCard from '../components/TemplateCard';
import SkeletonCard from '../components/SkeletonCard';
import Button from '../components/Button';
import ChatModal from '../components/ChatModal';
import { useAuth } from '../context/AuthContext';

const QUICK_CATEGORIES = [
  { label: 'All', id: 'All' },
  { label: 'UI/UX', id: 'ui-ux' },
  { label: 'Flyers', id: 'flyers' },
  { label: 'Branding', id: 'branding' },
  { label: 'Social Media', id: 'social-media' },
  { label: 'Mockups', id: 'mockups' },
  { label: 'Icons', id: 'icons' },
  { label: 'Logos', id: 'logos' },
];

export default function Home() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const category = searchParams.get('category') || 'All';
  const q = searchParams.get('q') || '';
  const [quickQuery, setQuickQuery] = useState('');
  
  // Chat modal state for featured creator
  const [chatRecipient, setChatRecipient] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['templates', { category, q }],
    queryFn: () => fetchTemplates({ category, q, sort: 'trending' }),
  });

  const { data: topDesignersData } = useQuery({
    queryKey: ['topDesigners'],
    queryFn: fetchTopDesigners,
  });

  const { data: compData } = useQuery({
    queryKey: ['competitions-home'],
    queryFn: () => fetchCompetitions({ limit: 2 }),
  });

  const { data: featuredData } = useQuery({
    queryKey: ['featured-templates'],
    queryFn: fetchFeaturedTemplates,
  });

  const templates = data?.templates || [];
  const topDesigners = topDesignersData?.designers || [];
  const liveCompetitions = compData?.data || [];
  const boostedTemplates = featuredData?.featured || [];

  // Featured items for spotlights
  const featuredTemplate = boostedTemplates[0] || templates[0] || null;
  const designerOfTheWeek = topDesigners[0] || null;
  const liveArena = liveCompetitions[0] || null;

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (quickQuery.trim()) {
      navigate(`/browse?q=${encodeURIComponent(quickQuery.trim())}`);
    }
  };

  // Keyboard shortcut Ctrl+K / Cmd+K
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        const searchInput = document.getElementById('home-main-search');
        if (searchInput) {
          searchInput.focus();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <div style={{ paddingBottom: '96px' }}>
      <div className="page-container">
        
        {/* ── Minimal Editorial Hero ── */}
        <section style={{ padding: '64px 0 40px', textAlign: 'center' }}>
          <h1 style={{ 
            fontSize: 'clamp(2.4rem, 5.5vw, 3.75rem)', 
            fontWeight: 800, 
            letterSpacing: '-0.035em',
            lineHeight: 1.1,
            marginBottom: '16px',
            color: 'var(--text-primary)'
          }}>
            Curated digital assets for modern design.
          </h1>

          <p style={{ 
            maxWidth: '560px', 
            margin: '0 auto 36px', 
            fontSize: '1.05rem', 
            color: 'var(--text-secondary)',
            lineHeight: 1.5,
            fontWeight: 400
          }}>
            Discover and download verified design systems, UI kits, templates, and vector graphics by independent creators.
          </p>

          {/* Minimal Central Search Bar */}
          <form 
            onSubmit={handleSearchSubmit} 
            style={{ 
              maxWidth: '580px', 
              margin: '0 auto 28px', 
              position: 'relative' 
            }}
          >
            <div style={{
              display: 'flex',
              alignItems: 'center',
              background: 'var(--bg-surface)',
              border: '1px solid var(--border-medium)',
              borderRadius: '9999px',
              padding: '6px 18px',
              boxShadow: 'var(--shadow-sm)',
              transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
            }}>
              <Search size={18} style={{ color: 'var(--text-tertiary)', marginRight: '12px', flexShrink: 0 }} />
              <input 
                id="home-main-search"
                type="text" 
                placeholder="Search templates, UI kits, brand identity, vector packs..."
                value={quickQuery}
                onChange={e => setQuickQuery(e.target.value)}
                style={{
                  flex: 1,
                  background: 'transparent',
                  border: 'none',
                  outline: 'none',
                  fontSize: '0.9375rem',
                  color: 'var(--text-primary)',
                  fontFamily: 'inherit',
                  padding: '8px 0',
                }}
              />
              <span style={{
                fontSize: '11px',
                fontWeight: 600,
                padding: '3px 7px',
                borderRadius: '6px',
                background: 'var(--bg-input)',
                color: 'var(--text-tertiary)',
                border: '1px solid var(--border-subtle)',
                userSelect: 'none',
                marginLeft: '8px'
              }}>
                ⌘K
              </span>
            </div>
          </form>

          {/* Category Filter Pills */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            flexWrap: 'wrap',
            maxWidth: '800px',
            margin: '0 auto 48px'
          }}>
            {QUICK_CATEGORIES.map(cat => {
              const isActive = (cat.id === 'All' && (!category || category === 'All')) || category === cat.id;
              return (
                <button
                  key={cat.id}
                  onClick={() => navigate(cat.id === 'All' ? '/browse' : `/browse?category=${cat.id}`)}
                  style={{
                    padding: '6px 14px',
                    borderRadius: '9999px',
                    fontSize: '0.8125rem',
                    fontWeight: 500,
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                    background: isActive ? 'var(--text-primary)' : 'var(--bg-surface)',
                    color: isActive ? 'var(--bg-base)' : 'var(--text-secondary)',
                    border: `1px solid ${isActive ? 'var(--text-primary)' : 'var(--border-subtle)'}`,
                  }}
                >
                  {cat.label}
                </button>
              );
            })}
          </div>
        </section>

        {/* ── Spotlights: Minimal Cards ── */}
        <section style={{ marginBottom: '64px' }}>
          <div className="section-header" style={{ marginBottom: '20px' }}>
            <div>
              <div className="section-eyebrow">SPOTLIGHTS</div>
              <h2 className="section-title" style={{ fontSize: '1.25rem' }}>
                Featured Highlights
              </h2>
            </div>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: '20px'
          }}>
            
            {/* 1. Design of the Week */}
            {featuredTemplate && (
              <div style={{
                background: 'var(--bg-surface)',
                border: '1px solid var(--border-subtle)',
                borderRadius: 'var(--radius-card)',
                padding: '20px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                transition: 'border-color 0.2s ease',
              }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
                    <span style={{
                      fontSize: '0.6875rem',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                      color: 'var(--text-secondary)',
                      background: 'var(--bg-input)',
                      padding: '3px 8px',
                      borderRadius: '9999px',
                      border: '1px solid var(--border-subtle)'
                    }}>
                      Design of the Week
                    </span>
                    <span style={{ fontSize: '0.875rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                      {(!featuredTemplate.price_cents || featuredTemplate.is_free || isNaN(Number(featuredTemplate.price_cents)) || Number(featuredTemplate.price_cents) <= 0)
                        ? 'Free'
                        : `$${(Number(featuredTemplate.price_cents) / 100).toFixed(2)}`}
                    </span>
                  </div>

                  <div style={{
                    width: '100%',
                    height: '160px',
                    borderRadius: '10px',
                    overflow: 'hidden',
                    marginBottom: '14px',
                    background: 'var(--bg-input)'
                  }}>
                    <img 
                      src={featuredTemplate.preview_url || featuredTemplate.image || 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=800&auto=format&fit=crop&q=80'} 
                      alt={featuredTemplate.title}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  </div>

                  <h3 style={{ fontSize: '0.9375rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '4px' }}>
                    {featuredTemplate.title}
                  </h3>
                  <p style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', marginBottom: '16px' }}>
                    by @{featuredTemplate.designer_name || featuredTemplate.designer || 'buildive'}
                  </p>
                </div>

                <Link to={`/template/${featuredTemplate.slug || featuredTemplate.id}`} style={{ width: '100%' }}>
                  <Button variant="secondary" size="md" style={{ width: '100%' }}>
                    View Asset <ArrowRight size={14} />
                  </Button>
                </Link>
              </div>
            )}

            {/* 2. Designer of the Week */}
            {designerOfTheWeek && (
              <div style={{
                background: 'var(--bg-surface)',
                border: '1px solid var(--border-subtle)',
                borderRadius: 'var(--radius-card)',
                padding: '20px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
              }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
                    <span style={{
                      fontSize: '0.6875rem',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                      color: 'var(--text-secondary)',
                      background: 'var(--bg-input)',
                      padding: '3px 8px',
                      borderRadius: '9999px',
                      border: '1px solid var(--border-subtle)'
                    }}>
                      Featured Creator
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: 'var(--text-secondary)', fontSize: '0.75rem', fontWeight: 600 }}>
                      <Star size={13} fill="currentColor" />
                      <span>{designerOfTheWeek.trust_score || '4.9'}</span>
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
                    <img 
                      src={designerOfTheWeek.avatar_url || `https://api.dicebear.com/7.x/shapes/svg?seed=${designerOfTheWeek.username}`}
                      alt={designerOfTheWeek.full_name || designerOfTheWeek.username}
                      style={{ width: '48px', height: '48px', borderRadius: '50%', border: '1px solid var(--border-subtle)' }}
                    />
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <h3 style={{ fontSize: '0.9375rem', fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>
                          {designerOfTheWeek.full_name || designerOfTheWeek.username}
                        </h3>
                        <CheckCircle2 size={14} color="var(--success)" />
                      </div>
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>
                        @{designerOfTheWeek.username} • {designerOfTheWeek.specialization || 'Visual Designer'}
                      </span>
                    </div>
                  </div>

                  <p style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', marginBottom: '16px', lineHeight: 1.5 }}>
                    {designerOfTheWeek.bio || 'Creating brand systems, vector packs, and design tokens for modern creative teams.'}
                  </p>
                </div>

                <div style={{ display: 'flex', gap: '8px' }}>
                  <Link to={`/designer/${designerOfTheWeek.username}`} style={{ flex: 1 }}>
                    <Button variant="secondary" size="md" style={{ width: '100%' }}>
                      Profile
                    </Button>
                  </Link>
                  <Button 
                    variant="primary" 
                    size="md"
                    onClick={() => setChatRecipient({
                      id: designerOfTheWeek.id || designerOfTheWeek.user_id,
                      name: designerOfTheWeek.full_name || designerOfTheWeek.username,
                      avatar: designerOfTheWeek.avatar_url
                    })}
                  >
                    <MessageSquare size={14} /> Message
                  </Button>
                </div>
              </div>
            )}

            {/* 3. Tournament Arena */}
            <div style={{
              background: 'var(--bg-surface)',
              border: '1px solid var(--border-subtle)',
              borderRadius: 'var(--radius-card)',
              padding: '20px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
            }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
                  <span style={{
                    fontSize: '0.6875rem',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    color: 'var(--text-secondary)',
                    background: 'var(--bg-input)',
                    padding: '3px 8px',
                    borderRadius: '9999px',
                    border: '1px solid var(--border-subtle)'
                  }}>
                    Tournament Arena
                  </span>
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>
                    Community Voting
                  </span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                  <div style={{
                    width: '38px',
                    height: '38px',
                    borderRadius: '10px',
                    background: 'var(--bg-input)',
                    border: '1px solid var(--border-subtle)',
                    color: 'var(--text-primary)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <Trophy size={18} />
                  </div>
                  <div>
                    <h3 style={{ fontSize: '0.9375rem', fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>
                      {liveArena?.title || 'Motion Loop & Brand Arena'}
                    </h3>
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>
                      Prize: {liveArena?.prize_pool || 'GH₵ 3,500'} • {liveArena?.entry_count || 12} Contenders
                    </span>
                  </div>
                </div>

                <p style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', marginBottom: '16px', lineHeight: 1.5 }}>
                  Designers submit work for public and academy evaluation. Vote to support creative talent.
                </p>
              </div>

              <Link to={liveArena ? `/competitions/${liveArena.id}` : '/competitions'}>
                <Button variant="secondary" size="md" style={{ width: '100%' }}>
                  Enter Arena & Vote <ArrowRight size={14} />
                </Button>
              </Link>
            </div>

          </div>
        </section>

        {/* ── Curated Marketplace Grid ── */}
        <section style={{ marginBottom: '64px' }}>
          <div className="section-header">
            <div>
              <div className="section-eyebrow">MARKETPLACE</div>
              <h2 className="section-title">
                Trending Templates
              </h2>
            </div>
            <Link to="/browse">
              <Button variant="secondary" size="sm">
                View all <ChevronRight size={14} />
              </Button>
            </Link>
          </div>

          <div className="template-grid">
            {isLoading ? (
              Array.from({ length: 8 }).map((_, i) => <SkeletonCard key={i} />)
            ) : templates.length > 0 ? (
              templates.slice(0, 8).map((t, idx) => (
                <TemplateCard 
                  key={t.id} 
                  id={t.id}
                  title={t.title}
                  slug={t.slug}
                  designer={t.designer || t.designer_name}
                  likes={t.like_count}
                  rating={t.avg_rating}
                  downloads={t.download_count}
                  price={t.price_cents ? t.price_cents / 100 : 0}
                  formats={['PSD', 'AI', 'FIG']} 
                  image={t.preview_url || t.image}
                  index={idx}
                />
              ))
            ) : (
              <div style={{ gridColumn: '1 / -1', color: 'var(--text-secondary)', textAlign: 'center', padding: '40px 0' }}>
                No templates found.
              </div>
            )}
          </div>
        </section>

        {/* ── Top Verified Master Designers ── */}
        {topDesigners.length > 0 && (
          <section style={{ marginBottom: '64px' }}>
            <div className="section-header">
              <div>
                <div className="section-eyebrow">COMMUNITY</div>
                <h2 className="section-title">
                  Featured Designers
                </h2>
              </div>
              <Link to="/community">
                <Button variant="secondary" size="sm">
                  Community Hub <ChevronRight size={14} />
                </Button>
              </Link>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: '20px'
            }}>
              {topDesigners.map((designer) => (
                <DesignerCard key={designer.id} designer={designer} />
              ))}
            </div>
          </section>
        )}

      </div>

      {/* Direct Messaging Chat Modal */}
      {chatRecipient && (
        <ChatModal 
          recipient={chatRecipient} 
          onClose={() => setChatRecipient(null)} 
        />
      )}
    </div>
  );
}
